/**
 * Intro-tour voor chatters (18-10-2026, Jordy).
 *
 * De teksten komen uit src/content/chatter-guide/guide.en.md: onder elke
 * `<!-- tour:naam -->` staat een kop en een eerste alinea; die alinea is de
 * tourtekst. Eén bron voor de tour, de Help-assistent en de PDF. Welke
 * stappen er zijn en in welke volgorde bewaakt tests/test_chatter_guide.py.
 *
 * Twee delen, zonder nepdata en zonder iets te versturen:
 *   A  op Chats (Pro): grootte, modelkaarten, balk, Live Feed, queues,
 *      kleuren, en tot slot "open een chat";
 *   B  in het chatvenster: teruglezen (nadrukkelijk), fan-info, notities,
 *      Transactions/History, Spending behavior, model-info, de Content
 *      Library (de tour opent de modal zelf: folder + zoeken, bundel,
 *      Free/Paid, prijs) en vertalen.
 * 19-10-2026 (Jordy): IN ÉÉN KEER. Na "Open a chat" (einde A) opent de tour
 * zelf een chat met `?tour=1` en loopt B door. In die tour-modus wordt er
 * NIETS als gelezen gemarkeerd (zie ChatViewProps.tour en get_conversation).
 * Een stap waarvan het anker er niet is (geen recht, Easy-stand) valt weg.
 */
import gids from '../../../src/content/chatter-guide/guide.en.md?raw'
import { isMobiel } from './mobiel'

export type TourDeel = 'A' | 'B'

export interface TourStap {
  /** De naam in de handleiding (`<!-- tour:naam -->`). */
  naam: string
  /** CSS-selector van het anker op de pagina. */
  anker: string
  kant?: 'top' | 'right' | 'bottom' | 'left'
  /** Nadrukkelijk tonen (oranje rand, grotere titel): "Always read back". */
  nadruk?: boolean
  /** Het anker verschijnt pas tijdens de tour (in de Content Library-modal):
   *  niet vooraf wegfilteren. */
  laat?: boolean
  /** Bij "Next": de Content Library openen (via de echte 🖼-knop) of sluiten. */
  naNext?: 'open-kiezer' | 'sluit-kiezer'
  /** Bij "Back": de Content Library sluiten (terug naar de 🖼-stap). */
  naBack?: 'sluit-kiezer'
}

/** ui_prefs-sleutels: deel A / deel B gezien (ook na overslaan). */
export const TOUR_GEZIEN: Record<TourDeel, string> = {
  A: 'tour.chats.done',
  B: 'tour.chat.done',
}

/** Event waarmee de Help-knop de tour (opnieuw) start, voor iedereen. */
export const TOUR_START = 'novahub:start-tour'

export const STAPPEN: Record<TourDeel, TourStap[]> = {
  A: [
    { naam: 'dashboard-size', anker: '[data-tour="dashboard-size"]', kant: 'bottom' },
    { naam: 'model-cards', anker: '[data-tour="model-cards"]', kant: 'bottom' },
    { naam: 'open-chats-bar', anker: '[data-tour="open-chats-bar"]', kant: 'bottom' },
    { naam: 'live-feed', anker: '[data-tour="live-feed"]', kant: 'top' },
    { naam: 'waiting-queue', anker: '[data-tour="waiting-queue"]', kant: 'right' },
    { naam: 'spenders', anker: '[data-tour="spenders"]', kant: 'left' },
    { naam: 'my-chats', anker: '[data-tour="my-chats"]', kant: 'right' },
    { naam: 'private', anker: '[data-tour="private"]', kant: 'left' },
    { naam: 'colours', anker: '[data-tour="open-chats-bar"]', kant: 'bottom' },
    { naam: 'open-chat', anker: '[data-tour="waiting-queue"]', kant: 'right' },
  ],
  // 18-10-2026 (Jordy, aanscherping): eerst nadrukkelijk teruglezen, dan
  // fan- en modelinfo per onderdeel, dan content (Free/Paid pas ná het
  // openen van de Content Library, daar ook credits/euro), dan vertalen.
  // Alleen echte onderdelen aanwijzen, niets aanklikken.
  B: [
    { naam: 'read-back', anker: '[data-tour="read-back"]', kant: 'right', nadruk: true },
    { naam: 'fan-info', anker: '[data-testid="fan-kolom"]', kant: 'left' },
    { naam: 'important-notes', anker: '[data-testid="fan-notities"]', kant: 'left' },
    { naam: 'fan-history', anker: '[data-tour="fan-tabs"]', kant: 'bottom' },
    { naam: 'spending', anker: '[data-tour="spending"]', kant: 'top' },
    { naam: 'model-info', anker: '[data-testid="model-kolom"]', kant: 'left' },
    // 19-10-2026 (Jordy): de Content Library zelf laten zien. "Next" op de
    // 🖼-stap opent de modal (alleen lezen: mappen en items); de modal-stappen
    // wijzen echte onderdelen aan; "Next" op de laatste sluit hem weer.
    { naam: 'content-folders', anker: '[data-tour="content-folders"]', kant: 'top', naNext: 'open-kiezer' },
    { naam: 'content-filter', anker: '[data-tour="content-filter"]', kant: 'bottom', laat: true, naBack: 'sluit-kiezer' },
    { naam: 'content-bundle', anker: '[data-tour="content-bundle"]', kant: 'top', laat: true },
    { naam: 'content-free-paid', anker: '[data-testid="betaalkeuze"]', kant: 'top', laat: true },
    { naam: 'content-price', anker: '[data-testid="betaalkeuze"]', kant: 'top', laat: true, naNext: 'sluit-kiezer' },
    { naam: 'translate', anker: '[data-testid="vertaalknop"]', kant: 'top' },
  ],
}

/** Het anker waarop een deel wacht voordat het start (de pagina is er dan). */
export const STARTANKER: Record<TourDeel, string> = {
  A: '[data-tour="live-feed"]',
  B: '[data-testid="vertaalknop"]',
}

export interface TourTekst { titel: string; tekst: string }

/** Kop + eerste alinea per `<!-- tour:naam -->` uit een handleiding. */
export function leesTourTeksten(md: string): Record<string, TourTekst> {
  const uit: Record<string, TourTekst> = {}
  const re = /<!-- tour:([a-z-]+) -->\s*\n#+ ([^\n]+)\n\s*\n([\s\S]+?)(?:\n\s*\n|$)/g
  for (const m of md.matchAll(re)) {
    uit[m[1]] = {
      titel: m[2].replace(/\s*\(priority \d+\)\s*$/i, '').replace(/^\d+\.\s*/, '').trim(),
      tekst: m[3].split(/\s+/).join(' ').replace(/\*\*(.+?)\*\*/g, '$1').trim(),
    }
  }
  return uit
}

export const TOURTEKSTEN = leesTourTeksten(gids)

/** De hele handleiding (voor "Open guide" in het Help-paneel). */
export const GIDS_MD: string = gids

/** De stappen van een deel die op deze pagina echt een anker hebben. */
/** Stappen die alleen op desktop bestaan: de grootte-keuze (Dashboard size)
 *  is op de telefoon verborgen, daar zoomt het dashboard niet (lib/schaal). */
const ALLEEN_DESKTOP = new Set(['dashboard-size'])

/** Op de telefoon (19-10-2026, Jordy V5): geen desktop-stappen, en geen stap
 *  waarvan het anker wel bestaat maar onzichtbaar is (display:none in mobiel.css). */
function opTelefoonTonen(s: TourStap, doc: Document): boolean {
  if (ALLEEN_DESKTOP.has(s.naam)) return false
  if (s.laat) return true
  const el = doc.querySelector(s.anker)
  return !!el && el.getClientRects().length > 0
}

export function aanwezigeStappen(deel: TourDeel, doc: Document = document): TourStap[] {
  const mobiel = isMobiel()
  const vroeg = STAPPEN[deel].filter((s) => TOURTEKSTEN[s.naam] && (s.laat || doc.querySelector(s.anker))
    && (!mobiel || opTelefoonTonen(s, doc)))
  // Zonder 🖼-knop (geen Content Library) ook geen modal-stappen.
  return doc.querySelector('[data-tour="content-folders"]') ? vroeg : vroeg.filter((s) => !s.laat)
}

/** De Content Library-modal: open (via de echte 🖼-knop) of sluiten (Close). */
const KIEZER_OPEN = '[data-tour="content-filter"]'
export function kiezerOpen(): boolean { return !!document.querySelector(KIEZER_OPEN) }
export async function openKiezer(): Promise<boolean> {
  if (kiezerOpen()) return true
  ;(document.querySelector('[data-tour="content-folders"]') as HTMLElement | null)?.click()
  const er = await wachtOp(KIEZER_OPEN, 8000)
  // De modal zakt nog even op zijn plek terwijl mappen en items laden.
  await new Promise((r) => window.setTimeout(r, 450))
  return er
}
export async function sluitKiezer(): Promise<void> {
  const filter = document.querySelector(KIEZER_OPEN)
  const kaart = filter?.closest('.card')
  const knop = kaart?.querySelector('button[aria-label="Close"]') as HTMLElement | null
  knop?.click()
  for (let i = 0; i < 20 && kiezerOpen(); i++) await new Promise((r) => window.setTimeout(r, 100))
}

/** Wacht tot een selector bestaat (of geeft na `ms` op). 45 s: bij een trage
 *  eerste laadbeurt startte de tour anders dat bezoek niet (18-10-2026). */
export function wachtOp(selector: string, ms = 45000): Promise<boolean> {
  return new Promise((klaar) => {
    const begin = Date.now()
    const kijk = () => {
      if (document.querySelector(selector)) return klaar(true)
      if (Date.now() - begin > ms) return klaar(false)
      window.setTimeout(kijk, 250)
    }
    kijk()
  })
}

/**
 * Start een deel. driver.js wordt pas hier geladen (eigen chunk), zodat
 * pagina's zonder tour er niet op wachten. `klaar` volgt bij Done, het kruisje
 * of Esc: overslaan telt ook als gezien; de Help-knop start hem opnieuw.
 */
export async function startTour(
  deel: TourDeel,
  klaar: (voltooid: boolean) => void,
  extra: { laatsteKnop?: () => string } = {},
): Promise<boolean> {
  const stappen = aanwezigeStappen(deel)
  if (!stappen.length) return false
  const [{ driver }] = await Promise.all([
    import('../vendor/driverjs/driver.js.mjs'),
    import('../vendor/driverjs/driver.css'),
  ])
  // Voltooid = op de laatste stap op Done/Got it geklikt (niet overgeslagen).
  let voltooid = false
  // Tijdens het openen/sluiten van de Content Library geen tweede klik
  // verwerken: anders slaat de tour stappen over.
  let wacht = false
  const daarna = (stap: Promise<unknown>, verder: () => void) => {
    if (wacht) return
    wacht = true
    void stap.then(() => { wacht = false; verder() })
  }
  const d = driver({
    showProgress: true,
    progressText: '{{current}} of {{total}}',
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: deel === 'A' ? 'Got it' : 'Done',
    popoverClass: 'novatour',
    // Klikken op een gemarkeerde knop doet niets tijdens de tour: er kan dus
    // nooit per ongeluk iets naar een fan.
    disableActiveInteraction: true,
    stagePadding: 6,
    stageRadius: 10,
    overlayOpacity: 0.6,
    onNextClick: () => {
      if (d.isLastStep()) voltooid = true
      d.moveNext()
    },
    // De tekst op de knop van de laatste stap pas bepalen als die stap er is
    // (de wachtrij kan bij de start nog aan het laden zijn).
    onPopoverRender: (popover) => {
      if (extra.laatsteKnop && d.isLastStep()) popover.nextButton.textContent = extra.laatsteKnop()
    },
    steps: stappen.map((s) => ({
      element: s.anker,
      // In de modal kan de inhoud nog verschuiven (miniaturen laden): de
      // markering kort daarna opnieuw meten.
      ...(s.laat ? { onHighlighted: () => { window.setTimeout(() => d.refresh(), 300) } } : {}),
      popover: {
        title: TOURTEKSTEN[s.naam].titel,
        description: TOURTEKSTEN[s.naam].tekst,
        side: s.kant,
        ...(s.nadruk ? { popoverClass: 'novatour nadruk' } : {}),
        ...(s.naNext ? {
          onNextClick: () => daarna(s.naNext === 'open-kiezer' ? openKiezer() : sluitKiezer(), () => d.moveNext()),
        } : {}),
        ...(s.naBack ? {
          onPrevClick: () => daarna(sluitKiezer(), () => d.movePrevious()),
        } : {}),
      },
    })),
    onDestroyed: () => {
      // Nooit een open Content Library achterlaten.
      if (kiezerOpen()) void sluitKiezer()
      klaar(voltooid)
    },
  })
  d.drive()
  return true
}

/** De eerste chat op de pagina die de tour zelf kan openen (Waiting queue eerst). */
export function eersteChatId(doc: Document = document): string | null {
  for (const sel of ['[data-tour="waiting-queue"]', '[data-tour="my-chats"]', '[data-tour="spenders"]',
                     '[data-tour="live-feed"]', 'body']) {
    const a = doc.querySelector(`${sel} a[href*="/conversations/show/"]`) as HTMLAnchorElement | null
    const m = a ? /\/conversations\/show\/(\d+)/.exec(a.getAttribute('href') || '') : null
    if (m) return m[1]
  }
  return null
}
